/**
 * Folders view: a signed container (.asice/.bdoc/.ddoc) is a FILE, not a folder.
 *
 * Renders it as a single signed-container row — seal icon, real extension, and
 * the SAME date and Uploader cells every other file in the list shows — whose title click
 * opens the shared signature panel instead of navigating into a folder. The
 * chevron expands a dropdown of the enclosed documents rather than a folder
 * level. Everything container-specific (documents, signatures, the ASiC-E
 * label) lives in that panel, not inline.
 *
 * Plain `.zip` archives keep the folder-like `ContainerFolderRow`.
 */

import { Loader2 } from "lucide-react";

import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { DocumentActions } from "../molecules/DocumentActions";
import { DocumentCheckbox } from "../molecules/DocumentCheckbox";
import { EnclosedDocumentsMenu } from "../molecules/EnclosedDocumentsMenu";
import { DocumentDateCell } from "../atoms/DocumentDateCell";
import { DocumentUploaderCell } from "../atoms/DocumentUploaderCell";
import { SignedFileIcon } from "../atoms/SignedContainerIndicators";
import { summariseContainerLookup, type SignatureStatus } from "../../signing/signatureModel";
import { useSignedContainerInfoQuery } from "../../signing/useSignedContainerInfo";
import { useSignaturePanel } from "../../signing/signaturePanelContext";
import { isDocumentProcessing } from "../../utils/documentState";
import { rowSelectionToggle } from "../../utils/rowSelectionClick";
import { type DocumentColumnFlags, documentGridTemplate } from "../../utils/fitDocumentColumns";

/** The container document itself. */
interface SignedContainerDoc {
  /** Container document id. */
  id: string;
  /** Container filename, extension included (e.g. "contract-2026-07-24.asice"). */
  filename: string;
  /** Container size in bytes. */
  size?: number | null;
  /** Upload timestamp — the date cell renders it exactly as document rows do. */
  createdAt: string;
  /** The file's own metadata dates, for the date column's "File modified" basis. */
  originalCreatedAt?: string | null;
  contentModifiedAt?: string | null;
  /** Uploader, as shown on every other row in the list. */
  createdBy?: string | number | null;
  createdByName?: string | null;
  createdByAvatarUrl?: string | null;
  createdByColor?: string | null;
  /** Document processing state. */
  state: string | null;
}

interface SignedContainerSelection {
  isSelected?: boolean;
  showCheckbox?: boolean;
  onSelect?: (isSelected: boolean) => void;
}

interface SignedContainerActions {
  showActions?: boolean;
  onDownload?: () => void;
  onDelete?: () => void;
  deleteLabel?: string;
  /** Render the destructive action as a non-destructive Restore action (trash view). */
  isRestore?: boolean;
}

interface SignedContainerRowProps {
  document: SignedContainerDoc;
  selection?: SignedContainerSelection;
  actions?: SignedContainerActions;
  /**
   * The list's visible columns (the checkbox and actions come from `selection`
   * and `actions`). The row takes the document rows' grid, so its date and
   * Uploader line up under theirs; the columns a container has nothing for
   * stay empty.
   */
  columns?: Partial<Omit<DocumentColumnFlags, "showCheckbox" | "showActions">>;
  /** The Uploader column was asked for but did not fit: name them in the date's tooltip. */
  uploaderInDateHint?: boolean;
  className?: string;
}

const SELECTION_DEFAULTS = { isSelected: false, showCheckbox: true } as const;

export function SignedContainerRow({
  document,
  selection,
  actions,
  columns = {},
  uploaderInDateHint = false,
  className,
}: SignedContainerRowProps) {
  const { isSelected, showCheckbox, onSelect } = { ...SELECTION_DEFAULTS, ...selection };
  const showActions = actions?.showActions !== false;
  const flags: DocumentColumnFlags = {
    showCheckbox,
    showActions,
    showDateCreated: !!columns.showDateCreated,
    showUser: !!columns.showUser,
    showVersion: !!columns.showVersion,
    showStatus: !!columns.showStatus,
    showTags: !!columns.showTags,
    showParent: !!columns.showParent,
  };

  return (
    <div
      className={cn(
        "group grid items-center gap-4 border-b px-6 py-1.5 transition-colors",
        isSelected ? "border-primary/50 bg-primary/5 hover:bg-primary/10" : "hover:bg-muted/50",
        className
      )}
      style={{ gridTemplateColumns: documentGridTemplate(flags) }}
      onClick={rowSelectionToggle(isSelected, onSelect)}
    >
      {showCheckbox && (
        <DocumentCheckbox isSelected={isSelected} onSelect={(selected) => onSelect?.(selected)} />
      )}

      <ContainerSummary
        document={document}
        isProcessing={document.state !== null && isDocumentProcessing(document.state)}
      />

      {flags.showDateCreated && (
        <DocumentDateCell
          document={{
            created_at: document.createdAt,
            original_created_at: document.originalCreatedAt,
            document_content_modified_date: document.contentModifiedAt,
          }}
          uploaderName={uploaderInDateHint ? document.createdByName : null}
        />
      )}
      {flags.showUser && (
        <DocumentUploaderCell
          createdBy={document.createdBy}
          createdByName={document.createdByName}
          createdByAvatarUrl={document.createdByAvatarUrl}
          createdByColor={document.createdByColor}
        />
      )}
      {flags.showVersion && <div />}
      {flags.showStatus && <div />}
      {flags.showTags && <div />}
      {flags.showParent && <div />}

      {showActions && <RowActions actions={actions} />}
    </div>
  );
}

// DocumentActions takes both handlers; `showDownload`/`showDelete` hide the ones
// a given row doesn't wire up.
const noop = () => undefined;
const stopRowClick = (e: React.MouseEvent) => e.stopPropagation();

function RowActions({ actions = {} }: { actions?: SignedContainerActions }) {
  const { onDownload, onDelete, deleteLabel, isRestore } = actions;

  return (
    <div className="flex justify-end" data-actions onClick={stopRowClick}>
      <div className="opacity-0 transition-opacity group-hover:opacity-100">
        <DocumentActions
          onDownload={onDownload || noop}
          onDelete={onDelete || noop}
          showDownload={Boolean(onDownload)}
          showDelete={Boolean(onDelete)}
          deleteLabel={deleteLabel}
          isRestore={isRestore}
        />
      </div>
    </div>
  );
}

/** The seal always draws; an unresolved lookup gets the neutral seal, not a claim. */
function iconStatus(status: SignatureStatus | null): SignatureStatus {
  return status ?? "unverified";
}

/** The name cell: seal icon and the title line, one line high like every row. */
function ContainerSummary({
  document,
  isProcessing,
}: {
  document: SignedContainerDoc;
  isProcessing: boolean;
}) {
  const { id, filename } = document;
  const infoQuery = useSignedContainerInfoQuery(id);
  const { status } = summariseContainerLookup(infoQuery.isSuccess, infoQuery.data);

  return (
    <div className="flex min-w-0 items-center gap-3">
      <div className="flex flex-shrink-0">
        <SignedFileIcon filename={filename} status={iconStatus(status)} />
      </div>
      <div className="min-w-0 flex-1">
        <ContainerTitleRow id={id} filename={filename} isProcessing={isProcessing} />
      </div>
    </div>
  );
}

/** Filename (opens the panel) and the enclosed-documents chevron. The signature
 *  status is carried by the seal on the file icon alone — the lists show no
 *  written status. */
function ContainerTitleRow({
  id,
  filename,
  isProcessing,
}: {
  id: string;
  filename: string;
  isProcessing: boolean;
}) {
  const panel = useSignaturePanel();
  const openPanel = () => panel?.open({ containerId: id });

  const handleTitleClick = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.stopPropagation();
    openPanel();
  };

  return (
    <div className="flex min-w-0 items-center gap-2">
      <button
        type="button"
        data-row-title
        onClick={handleTitleClick}
        disabled={!panel}
        className={cn(
          "min-w-0 max-w-full truncate text-left text-sm font-medium",
          panel && "cursor-pointer hover:text-primary hover:underline"
        )}
      >
        {filename}
      </button>
      <EnclosedDocumentsMenu containerId={id} containerFilename={filename} />
      {isProcessing && <ExtractingNote />}
    </div>
  );
}

/** While the container is still being unzipped: beside the title, ellipsised,
 *  so the row stays one line. */
function ExtractingNote() {
  const { t } = useLanguage();

  return (
    <span className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
      <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin" />
      <span className="min-w-0 truncate">{t.documents.signedContainer.extracting}</span>
    </span>
  );
}
